import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/login', { username, password });
    return response.data;
  },

  register: async (name, username, email, password) => {
    const response = await api.post('/register', { name, username, email, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/logout');
    return response.data;
  },

  getUser: async () => {
    const response = await api.get('/user');
    return response.data;
  },
};

export const invoiceAPI = {
  getAll: async () => {
    const response = await api.get('/invoices');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  },

  create: async (invoiceData) => {
    const response = await api.post('/invoices', invoiceData);
    return response.data;
  },

  update: async (id, invoiceData) => {
    const response = await api.put(`/invoices/${id}`, invoiceData);
    return response.data;
  },

  cancel: async (id) => {
    const response = await api.patch(`/invoices/${id}/cancel`);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/invoices/${id}`);
    return response.data;
  },
};

export const sellerProfileAPI = {
  getAll: async () => {
    const response = await api.get('/seller-profiles');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/seller-profiles/${id}`);
    return response.data;
  },

  create: async (profileData) => {
    const response = await api.post('/seller-profiles', profileData);
    return response.data;
  },

  update: async (id, profileData) => {
    const response = await api.put(`/seller-profiles/${id}`, profileData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/seller-profiles/${id}`);
    return response.data;
  },
};

export const bankAccountAPI = {
  getAll: async () => {
    const response = await api.get('/bank-accounts');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/bank-accounts/${id}`);
    return response.data;
  },

  create: async (accountData) => {
    const response = await api.post('/bank-accounts', accountData);
    return response.data;
  },

  update: async (id, accountData) => {
    const response = await api.put(`/bank-accounts/${id}`, accountData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/bank-accounts/${id}`);
    return response.data;
  },
};

export const customerProfileAPI = {
  getAll: async () => {
    const response = await api.get('/customer-profiles');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/customer-profiles/${id}`);
    return response.data;
  },

  create: async (profileData) => {
    const response = await api.post('/customer-profiles', profileData);
    return response.data;
  },

  update: async (id, profileData) => {
    const response = await api.put(`/customer-profiles/${id}`, profileData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/customer-profiles/${id}`);
    return response.data;
  },
};

export const settingsAPI = {
  get: async () => {
    const response = await api.get('/settings');
    return response.data;
  },

  uploadLogo: async (file, variant = 'logo') => {
    const fd = new FormData();
    fd.append('logo', file);
    const response = await api.post(`/settings/${variant}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteLogo: async (variant = 'logo') => {
    const response = await api.delete(`/settings/${variant}`);
    return response.data;
  },
};

export const billSettingsAPI = {
  get: async () => {
    const response = await api.get('/bill-settings');
    return response.data;
  },

  update: async (data) => {
    const response = await api.put('/bill-settings', data);
    return response.data;
  },
};

export const noteTemplateAPI = {
  getAll: async () => {
    const response = await api.get('/note-templates');
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/note-templates', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/note-templates/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/note-templates/${id}`);
    return response.data;
  },
};

export const itemProfileAPI = {
  getAll: async () => {
    const response = await api.get('/item-profiles');
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/item-profiles', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/item-profiles/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/item-profiles/${id}`);
    return response.data;
  },
};

export const contactInquiryAPI = {
  getAll: async () => {
    const response = await api.get('/contact-inquiries');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/contact-inquiries/${id}`);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/contact-inquiries/${id}`);
    return response.data;
  },
};

export const contactInfoAPI = {
  get: async () => {
    const response = await api.get('/contact-info');
    return response.data;
  },

  update: async (data) => {
    const response = await api.put('/contact-info', data);
    return response.data;
  },
};

export const portfolioAPI = {
  // Admin: all portfolios including inactive
  adminGetAll: async () => {
    const response = await api.get('/admin/portfolios');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/portfolios/${id}`);
    return response.data;
  },

  // FormData is passed directly — axios sets Content-Type automatically
  create: async (formData) => {
    const response = await api.post('/portfolios', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Use POST /portfolios/{id}/update for multipart (file upload support)
  update: async (id, formData) => {
    const response = await api.post(`/portfolios/${id}/update`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/portfolios/${id}`);
    return response.data;
  },
};

export default api;
